/**
 * By-weight rate editor for the Service Pricing page.
 *
 * By-weight services (Wash & Fold, Wash & Iron, Personalised Wash) charge a
 * per-kg rate that lives on a `per_kg` service item - the SAME `service_items`
 * row the rest of this page edits. This is a clearer front door onto it, not a
 * second pricing system: it saves through the existing
 * PUT /admin/services/:id/items/:itemId endpoint.
 *
 * A rate change applies to NEW orders only. Existing order lines keep the
 * unit_price snapshotted when they were created, so no historical bill moves.
 */
import { useEffect, useState } from 'react'
import { Check, Pencil, X } from 'lucide-react'
import { useColors } from '../../hooks/useColors'
import { validateRate } from './rateValidation'

export type WeightRate = {
  serviceId: string
  serviceName: string
  itemId: string
  itemName: string
  price: number
  isActive: boolean
}

type Props = {
  rates: WeightRate[]
  canEdit: boolean
  onSave: (rate: WeightRate, newPrice: number) => Promise<void>
}

const formatRate = (value: number) => {
  const rounded = Math.round(Number(value || 0) * 100) / 100
  const hasPaise = Math.round(rounded * 100) % 100 !== 0
  return new Intl.NumberFormat('en-IN', {
    style: 'currency',
    currency: 'INR',
    minimumFractionDigits: hasPaise ? 2 : 0,
    maximumFractionDigits: 2,
  }).format(rounded)
}

export default function WeightRatesCard({ rates, canEdit, onSave }: Props) {
  const colors = useColors()
  const [editingId, setEditingId] = useState<string | null>(null)
  const [draft, setDraft] = useState('')
  const [fieldError, setFieldError] = useState('')
  const [savingId, setSavingId] = useState<string | null>(null)

  // Drop an open editor if the row it belongs to disappears (service toggled
  // off, catalog reloaded) so a stale draft can never be saved onto it.
  useEffect(() => {
    if (editingId && !rates.some((rate) => rate.itemId === editingId)) {
      setEditingId(null)
      setDraft('')
      setFieldError('')
    }
  }, [rates, editingId])

  if (!rates.length) return null

  const startEdit = (rate: WeightRate) => {
    setEditingId(rate.itemId)
    setDraft(String(rate.price))
    setFieldError('')
  }

  const cancelEdit = () => {
    setEditingId(null)
    setDraft('')
    setFieldError('')
  }

  const commit = async (rate: WeightRate) => {
    const parsed = validateRate(draft)
    if (!parsed.ok) {
      setFieldError(parsed.message)
      return
    }
    if (parsed.value === Math.round(rate.price * 100) / 100) {
      cancelEdit()
      return
    }
    setSavingId(rate.itemId)
    try {
      await onSave(rate, parsed.value)
      cancelEdit()
    } finally {
      setSavingId(null)
    }
  }

  const cell = { padding: '10px 12px', fontSize: 13, color: colors.textPrimary } as const

  return (
    <section
      style={{
        background: colors.bgCard,
        border: `1px solid ${colors.border}`,
        borderRadius: 12,
        padding: 14,
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
        <h3 className="font-display" style={{ margin: 0, color: colors.textPrimary, fontSize: 16 }}>
          By Weight rates
        </h3>
        <p className="font-body" style={{ margin: 0, fontSize: 12, color: colors.textMuted }}>
          Applies to new orders only. Existing orders keep the rate they were booked at.
        </p>
      </div>

      <div style={{ overflowX: 'auto', marginTop: 10 }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: colors.bgSecondary }}>
              {['Service', 'Category', 'Rate', ''].map((head, i) => (
                <th
                  key={head || `wr-col-${i}`}
                  className="font-body"
                  style={{ textAlign: i === 3 ? 'right' : 'left', padding: '8px 12px', fontSize: 11, fontWeight: 600, letterSpacing: '0.4px', textTransform: 'uppercase', color: colors.textMuted }}
                >
                  {head}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rates.map((rate) => {
              const isEditing = editingId === rate.itemId
              const isSaving = savingId === rate.itemId
              return (
                <tr key={rate.itemId} style={{ borderTop: `1px solid ${colors.border}`, opacity: rate.isActive ? 1 : 0.55 }}>
                  <td style={{ ...cell, fontWeight: 600 }}>
                    {rate.serviceName}
                    {rate.isActive ? null : (
                      <span className="font-body" style={{ marginLeft: 8, fontSize: 11, color: colors.textMuted }}>(inactive)</span>
                    )}
                  </td>
                  <td style={{ ...cell, color: colors.textMuted }}>
                    {/* The per-kg line's own name: "By weight", "Individual packing", … */}
                    {rate.itemName}
                  </td>
                  <td style={cell}>
                    {isEditing ? (
                      <div style={{ display: 'grid', gap: 4 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          <span style={{ color: colors.textMuted }}>₹</span>
                          <input
                            autoFocus
                            type="number"
                            min="0"
                            step="0.01"
                            inputMode="decimal"
                            value={draft}
                            aria-label={`Rate per kg for ${rate.serviceName} ${rate.itemName}`}
                            aria-invalid={Boolean(fieldError)}
                            onChange={(e) => { setDraft(e.target.value); setFieldError('') }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') { e.preventDefault(); void commit(rate) }
                              if (e.key === 'Escape') { e.preventDefault(); cancelEdit() }
                            }}
                            style={{ width: 110, border: `1px solid ${fieldError ? 'var(--color-error)' : colors.border}`, borderRadius: 8, padding: '6px 8px', background: colors.surface, color: colors.textPrimary }}
                          />
                          <span style={{ color: colors.textMuted }}>/ kg</span>
                        </div>
                        {fieldError ? (
                          <span role="alert" className="font-body" style={{ fontSize: 12, color: 'var(--color-error)' }}>{fieldError}</span>
                        ) : null}
                      </div>
                    ) : (
                      <span style={{ fontWeight: 600 }}>{formatRate(rate.price)} / kg</span>
                    )}
                  </td>
                  <td style={{ ...cell, textAlign: 'right', whiteSpace: 'nowrap' }}>
                    {!canEdit ? (
                      <span className="font-body" style={{ fontSize: 12, color: colors.textMuted }}>View only</span>
                    ) : isEditing ? (
                      <div style={{ display: 'inline-flex', gap: 6 }}>
                        {/* Explicit save - nothing is written while typing. */}
                        <button
                          type="button"
                          onClick={() => void commit(rate)}
                          disabled={isSaving}
                          style={{ border: 0, borderRadius: 8, background: colors.accent, color: colors.bgSidebar, padding: '6px 12px', fontWeight: 700, cursor: isSaving ? 'not-allowed' : 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                        >
                          <Check size={14} /> {isSaving ? 'Saving…' : 'Save'}
                        </button>
                        <button
                          type="button"
                          onClick={cancelEdit}
                          disabled={isSaving}
                          aria-label="Cancel rate edit"
                          style={{ border: `1px solid ${colors.border}`, borderRadius: 8, background: 'transparent', color: colors.textPrimary, padding: '6px 10px', cursor: 'pointer' }}
                        >
                          <X size={14} />
                        </button>
                      </div>
                    ) : (
                      <button
                        type="button"
                        onClick={() => startEdit(rate)}
                        style={{ border: `1px solid ${colors.border}`, borderRadius: 8, background: 'transparent', color: colors.textPrimary, padding: '6px 12px', fontWeight: 600, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 6 }}
                      >
                        <Pencil size={13} /> Edit
                      </button>
                    )}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </section>
  )
}
